import React, { useContext, useState } from "react";
import { gql, useMutation, useQuery } from "@apollo/client";
import { Accordion, AccordionSummary, AccordionDetails, Alert, Box, Button, Stack, TextField, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { UserContext } from "../../../../../platform/components/auth/auth-context";

const FIELDS = `internalNotes privateNotes internalRevision privateRevision canEditInternal`;
const GET_NOTES = gql`query BookingTypeNotes($bookingTypeId: Int!) {
  bookingTypeNotes(bookingTypeId: $bookingTypeId) { ${FIELDS} }
}`;
const SAVE_NOTE = gql`mutation SaveBookingTypeNote($bookingTypeId: Int!, $audience: BookingNoteAudience!, $content: String!, $expectedRevision: Int!) {
  saveBookingTypeNote(bookingTypeId: $bookingTypeId, audience: $audience, content: $content, expectedRevision: $expectedRevision) { ${FIELDS} }
}`;

function NoteField({ bookingTypeId, audience, initialValue, initialRevision, editable }) {
  const [content, setContent] = useState(initialValue);
  const [saved, setSaved] = useState(initialValue);
  const [revision, setRevision] = useState(initialRevision);
  const [message, setMessage] = useState("");
  const [save, { loading, error }] = useMutation(SAVE_NOTE, { fetchPolicy: "no-cache" });
  const personal = audience === "PRIVATE";
  async function handleSave() {
    setMessage("");
    try {
      const { data } = await save({ variables: { bookingTypeId: Number(bookingTypeId), audience, content, expectedRevision: revision } });
      setSaved(content);
      setRevision(data.saveBookingTypeNote[personal ? "privateRevision" : "internalRevision"]);
      setMessage("Saved.");
    } catch { /* Keep the draft and show the mutation error below. */ }
  }
  return <Stack spacing={1}>
    <Typography variant="subtitle2">{personal ? "Private notes" : "Internal notes"}</Typography>
    <Typography variant="body2" color="text.secondary">
      {personal ? "Only you can view and edit these notes, even in a team workspace." : "Visible to workspace members with booking-type or calendar access. Booking-type editors can update them."}
    </Typography>
    {editable ? <TextField
      label={personal ? "Your private notes" : "Team internal notes"}
      multiline minRows={3} fullWidth value={content}
      onChange={event => { setContent(event.target.value); setMessage(""); }}
      disabled={loading} inputProps={{ maxLength: 10000 }}
      helperText={`${content.length.toLocaleString()} / 10,000 characters`}
    /> : <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{content || "No notes yet."}</Typography>}
    {error && <Alert severity="error">{error.message}</Alert>}
    {editable && <Stack direction="row" spacing={1} alignItems="center">
      <Button size="small" variant="outlined" disabled={loading || content === saved} onClick={handleSave}>{loading ? "Saving…" : `Save ${personal ? "private" : "internal"} notes`}</Button>
      <Button size="small" disabled={loading || content === saved} onClick={() => { setContent(saved); setMessage(""); }}>Discard changes</Button>
      {message && <Typography role="status" variant="body2" color="success.main">{message}</Typography>}
    </Stack>}
  </Stack>;
}

function NotesPanel({ bookingTypeId, readOnly, expanded }) {
  const [generation, setGeneration] = useState(0);
  const { data, loading, error, refetch } = useQuery(GET_NOTES, {
    variables: { bookingTypeId: Number(bookingTypeId) }, fetchPolicy: "no-cache",
  });
  const notes = data?.bookingTypeNotes;
  async function reload() {
    if (!window.confirm("Reload saved notes? Unsaved changes in both fields will be discarded.")) return;
    try { await refetch(); setGeneration(value => value + 1); } catch { /* Query error is shown below. */ }
  }
  return <Accordion defaultExpanded={expanded} sx={{ mt: 2, boxShadow: "none", border: "1px solid", borderColor: "divider" }}>
    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
      <Stack direction="row" spacing={1} alignItems="center"><LockOutlinedIcon fontSize="small" /><Typography>Internal &amp; private notes</Typography></Stack>
    </AccordionSummary>
    <AccordionDetails>
      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">Notes about this booking type. Never shown to invitees, included in emails, or shared with external calendars.</Typography>
        {loading && <Typography variant="body2">Loading notes…</Typography>}
        {error && <Alert severity="error">Could not load notes. {error.message}<Button size="small" onClick={() => refetch()}>Retry</Button></Alert>}
        {notes && <React.Fragment key={generation}>
          <NoteField bookingTypeId={bookingTypeId} audience="INTERNAL" initialValue={notes.internalNotes} initialRevision={notes.internalRevision} editable={!readOnly && notes.canEditInternal} />
          <Box sx={{ borderTop: "1px solid", borderColor: "divider", pt: 2 }}>
            <NoteField bookingTypeId={bookingTypeId} audience="PRIVATE" initialValue={notes.privateNotes} initialRevision={notes.privateRevision} editable={!readOnly} />
          </Box>
          {!readOnly && <Button size="small" sx={{ alignSelf: "flex-start" }} onClick={reload}>Reload saved notes</Button>}
        </React.Fragment>}
      </Stack>
    </AccordionDetails>
  </Accordion>;
}

export default function BookingTypeNotes({ bookingTypeId, readOnly = false, expanded = false }) {
  const { providerUser } = useContext(UserContext);
  if (!bookingTypeId || !providerUser?.userId) return null;
  return <NotesPanel key={`${providerUser.userId}:${bookingTypeId}`} bookingTypeId={bookingTypeId} readOnly={readOnly} expanded={expanded} />;
}
